import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

/**
 * Theme preference store.
 *
 * Three states:
 *  - `dark`   — force dark chrome
 *  - `light`  — force light chrome
 *  - `system` — follow the OS `prefers-color-scheme`
 *
 * On every change we (a) set `data-theme` on the `<html>` element to drive
 * the CSS custom-property cascade in `main.css`, and (b) persist the choice
 * to localStorage so refreshes stay stable. The store is initialised once
 * at app boot from persisted value → system default (light as the admin's
 * house theme — the SYNC brand reads best on white surfaces).
 */

export type ThemePreference = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

const STORAGE_KEY = 'kori.admin.theme'

function readStoredPreference(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'light'
  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === 'dark' || raw === 'light' || raw === 'system') return raw
  return 'light'
}

function systemPreference(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', resolved)
}

export const useThemeStore = defineStore('theme', () => {
  const preference = ref<ThemePreference>(readStoredPreference())
  const systemResolved = ref<ResolvedTheme>(systemPreference())

  const resolved = computed<ResolvedTheme>(() =>
    preference.value === 'system' ? systemResolved.value : preference.value,
  )

  const isDark = computed(() => resolved.value === 'dark')

  function set(pref: ThemePreference): void {
    preference.value = pref
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, pref)
    }
  }

  /** Bi-modal toggle. `system` is promoted to its opposite so the button
   * always flips visually. */
  function toggle(): void {
    set(resolved.value === 'dark' ? 'light' : 'dark')
  }

  // React to OS preference changes while the tab is open.
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    mq.addEventListener?.('change', (e) => {
      systemResolved.value = e.matches ? 'light' : 'dark'
    })
  }

  // Apply on every change. Runs synchronously once on first read.
  watch(
    resolved,
    (next) => {
      applyTheme(next)
    },
    { immediate: true },
  )

  return { preference, resolved, isDark, set, toggle }
})
